import * as React from "react";
import Grid from "@mui/material/Grid";
import List from "@mui/material/List";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import Checkbox from "@mui/material/Checkbox";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";

// Utility functions
function not(a, b) {
  return a.filter((value) => !b.includes(value));
}

function intersection(a, b) {
  return a.filter((value) => b.includes(value));
}

function union(a, b) {
  return [...a, ...not(b, a)];
}

function excludeHidden(employees) {
  return employees.filter((employee) => !employee.isHidden);
}

// Helper to obtain a consistent key
function getEmployeeKey(employee) {
  // Use `_id` if available; otherwise, fall back to `driverId`
  return employee._id || employee.driverId;
}

// Main component
export default function EmployeeTransferList({
  allEmployees = [],
  attendees = [],
  onChange,
}) {
  // Filter employees who are not in the attendees list
  // ... existing code ...

  const notAttending = (a = [], b = []) => {
    return a
      .filter(
        (employee) =>
          employee.isHidden !== true &&
          employee.isHidden !== "true" &&
          !b.some((attendee) => attendee.driverId === employee._id)
      )
      .sort((a, b) => a.lastName.localeCompare(b.lastName));
  };

  const initialLeft = notAttending(allEmployees, attendees);
  const [checked, setChecked] = React.useState([]);
  const [left, setLeft] = React.useState(initialLeft);
  const [right, setRight] = React.useState(attendees);

  const leftChecked = intersection(checked, left);
  const rightChecked = intersection(checked, right);

  // Handlers for selecting items
  const handleToggle = (value) => () => {
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
  };

  const numberOfChecked = (items) => intersection(checked, items).length;

  const handleToggleAll = (items) => () => {
    if (numberOfChecked(items) === items.length) {
      setChecked(not(checked, items));
    } else {
      setChecked(union(checked, items));
    }
  };

  const handleCheckedRight = () => {
    const updatedAttendees = [...right, ...leftChecked];
    setRight(updatedAttendees);
    setLeft(not(left, leftChecked));
    setChecked(not(checked, leftChecked));
    onChange(updatedAttendees); // Trigger the callback
  };

  const handleCheckedLeft = () => {
    const updatedAttendees = not(right, rightChecked);
    setLeft([...left, ...rightChecked]);
    setRight(updatedAttendees);
    setChecked(not(checked, rightChecked));
    onChange(updatedAttendees); // Trigger the callback
  };

  // Custom list renderer
  const customList = (title, items, nameFields) => (
    <Card>
      <CardHeader
        sx={{ px: 2, py: 1 }}
        avatar={
          <Checkbox
            onClick={handleToggleAll(items)}
            checked={
              numberOfChecked(items) === items.length && items.length !== 0
            }
            indeterminate={
              numberOfChecked(items) !== items.length &&
              numberOfChecked(items) !== 0
            }
            disabled={items.length === 0}
            inputProps={{
              "aria-label": "all items selected",
            }}
          />
        }
        title={title}
        subheader={`${numberOfChecked(items)}/${items.length} selected`}
      />
      <Divider />
      <List
        sx={{
          width: 350,
          height: 300,
          bgcolor: "background.paper",
          overflow: "auto",
        }}
        dense
        component="div"
        role="list"
      >
        {items.map((value) => {
          const key = getEmployeeKey(value); // Ensure consistent unique key
          const labelId = `transfer-list-all-item-${key}-label`;
          const primaryText = `${value[nameFields[0]]}, ${
            value[nameFields[1]]
          }`;

          return (
            <ListItem
              key={key}
              role="listitem"
              button
              onClick={handleToggle(value)}
            >
              <ListItemIcon>
                <Checkbox
                  checked={checked.indexOf(value) !== -1}
                  tabIndex={-1}
                  disableRipple
                  inputProps={{
                    "aria-labelledby": labelId,
                  }}
                />
              </ListItemIcon>
              <ListItemText id={labelId} primary={primaryText} />
            </ListItem>
          );
        })}
      </List>
    </Card>
  );

  return (
    <Grid container spacing={2} justifyContent="center" alignItems="center">
      <Grid item>
        {customList("All Employees", left, ["lastName", "firstName"])}
      </Grid>
      <Grid item>
        <Grid container direction="column" alignItems="center">
          <Button
            sx={{ my: 0.5 }}
            variant="outlined"
            size="small"
            onClick={handleCheckedRight}
            disabled={leftChecked.length === 0}
            aria-label="move selected right"
          >
            &gt;
          </Button>
          <Button
            sx={{ my: 0.5 }}
            variant="outlined"
            size="small"
            onClick={handleCheckedLeft}
            disabled={rightChecked.length === 0}
            aria-label="move selected left"
          >
            &lt;
          </Button>
        </Grid>
      </Grid>
      <Grid item>
        {customList("Employees in Attendance", right, [
          "lastName",
          "firstName",
        ])}
      </Grid>
    </Grid>
  );
}
